import { getGrid, type GridGeometry } from '../../grid';
import type { MapDefinition } from '../../model/gamestate/GameState';
import { pathAutotiler } from '../../terrain/pathAutotiler/activePathAutotiler';
import {
    getPathTileQuarterAtlasDefinition,
    loadPathTileAtlasImage,
} from '../../terrain/pathTileRegistry';
import { getTerrainTexturePath, type TerrainTextureKey } from '../../terrain/registry';
import {
    RenderBoard,
    resolveChunkTileSize,
    type BoardChunk,
    type ChunkTileSizeOptions,
} from '../tilemap/board';
import { COLORS } from '../tilemap/constants';
import {
    drawGridClippedPathTileImage,
    resolvePathTileDrawGeometry,
    resolvePathTileTransformCenter,
} from '../tilemap/pathTileRenderGeometry';
import { drawSquarePathOverlay, prepareSquarePathAtlas } from '../tilemap/squarePathOverlay';
import { getTerrainTexture, getTileTextureKey, subscribeToTerrainTextureUpdates } from '../tilemap/terrainTextures';
import type { Viewport } from '../tilemap/viewport';

const TERRAIN_CHUNK_PRELOAD = 1;
const TERRAIN_CHUNK_GUTTER_TILES = 1;
const TERRAIN_TARGET_CHUNK_PIXEL_SIZE = 768;
const TERRAIN_PREFERRED_CHUNK_TILE_SIZES = [16, 9, 6, 4] as const;

interface TerrainRenderDebugStats {
    chunkTileSize: number;
    visibleChunks: number;
    rebuiltChunks: number;
    rebuiltChunkKeys: string[];
    pendingTextureTiles: number;
    rebuildMs: number;
    drawMs: number;
    drawBlitCount: number;
    drawBlitArea: number;
    maxBlitArea: number;
    authoredMs: number;
    terrainBaseMs: number;
    squarePathMs: number;
    gridStrokeMs: number;
    pathTileLayerMs: number;
}

interface TerrainChunkPhaseStats {
    authoredMs: number;
    terrainBaseMs: number;
    squarePathMs: number;
    gridStrokeMs: number;
    pathTileLayerMs: number;
    pendingTextureTiles: number;
}

function intersectWorldRects(a: { left: number; top: number; right: number; bottom: number }, b: { left: number; top: number; right: number; bottom: number }) {
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    if (right <= left || bottom <= top) {
        return null;
    }
    return {
        left,
        top,
        right,
        bottom,
        width: right - left,
        height: bottom - top,
    };
}

export class World2DTerrainLevelRenderer {
    private tileSize: number;
    private readonly requestRender: () => void;
    private grid: GridGeometry;
    private map: MapDefinition;
    private board: RenderBoard;
    private readonly backgroundCanvas: HTMLCanvasElement;
    private readonly backgroundCtx: CanvasRenderingContext2D | null;
    private devicePixelRatio = 1;
    private viewportWidth = 1;
    private viewportHeight = 1;
    private readonly chunkTextures = new Map<string, HTMLCanvasElement>();
    private readonly dirtyChunkKeys = new Set<string>();
    private readonly textureChunkKeys = new Map<TerrainTextureKey, Set<string>>();
    private readonly unsubscribeTerrainTextureUpdates: () => void;
    private readonly pathTileAtlasImages = new Map<string, HTMLImageElement>();
    private readonly chunkTileSizeOptions: ChunkTileSizeOptions;
    private lastDebugStats: TerrainRenderDebugStats = {
        chunkTileSize: 0,
        visibleChunks: 0,
        rebuiltChunks: 0,
        rebuiltChunkKeys: [],
        pendingTextureTiles: 0,
        rebuildMs: 0,
        drawMs: 0,
        drawBlitCount: 0,
        drawBlitArea: 0,
        maxBlitArea: 0,
        authoredMs: 0,
        terrainBaseMs: 0,
        squarePathMs: 0,
        gridStrokeMs: 0,
        pathTileLayerMs: 0,
    };
    constructor(
        tileSize: number,
        map: MapDefinition,
        backgroundCanvas: HTMLCanvasElement,
        requestRender: () => void,
        options?: {
            chunkTileSizeOptions?: ChunkTileSizeOptions;
        },
    ) {
        this.tileSize = tileSize;
        this.requestRender = requestRender;
        this.map = map;
        this.grid = getGrid(map.gridType);
        this.chunkTileSizeOptions = options?.chunkTileSizeOptions ?? {};
        this.board = this.createBoard(map, this.grid, this.tileSize);
        this.backgroundCanvas = backgroundCanvas;
        this.backgroundCtx = backgroundCanvas.getContext('2d');
        if (this.backgroundCtx) {
            this.backgroundCtx.imageSmoothingEnabled = false;
        }
        this.unsubscribeTerrainTextureUpdates = subscribeToTerrainTextureUpdates((key) => {
            this.invalidateTextureChunks(key);
        });
        prepareSquarePathAtlas(() => this.invalidateMapTexture());
        void this.preloadPathTileAtlases();
        this.rebuildTextureChunkIndex();
        this.invalidateAllChunks();
    }

    public setMap(map: MapDefinition): void {
        this.map = map;
        this.grid = getGrid(map.gridType);
        this.board = this.createBoard(map, this.grid, this.tileSize);
        this.chunkTextures.clear();
        this.dirtyChunkKeys.clear();
        this.textureChunkKeys.clear();
        void this.preloadPathTileAtlases();
        this.rebuildTextureChunkIndex();
        this.invalidateAllChunks();
    }

    public getMap(): MapDefinition {
        return this.map;
    }

    public getGrid(): GridGeometry {
        return this.grid;
    }

    public setTileSize(tileSize: number): void {
        if (!Number.isFinite(tileSize) || tileSize <= 0 || this.tileSize === tileSize) {
            return;
        }
        this.tileSize = tileSize;
        this.board = this.createBoard(this.map, this.grid, this.tileSize);
        this.chunkTextures.clear();
        this.dirtyChunkKeys.clear();
        this.textureChunkKeys.clear();
        this.rebuildTextureChunkIndex();
        this.invalidateAllChunks();
    }

    public invalidateMapTexture(): void {
        this.invalidateAllChunks();
    }

    public getTileSize(): number {
        return this.tileSize;
    }

    public getResidentChunkCount(): number {
        return this.chunkTextures.size;
    }

    public getChunkTexture(chunkKey: string): HTMLCanvasElement | null {
        return this.chunkTextures.get(chunkKey) ?? null;
    }

    public getBackgroundCanvas(): HTMLCanvasElement {
        return this.backgroundCanvas;
    }

    public prepareVisibleChunks(
        viewport: Viewport,
        options?: {
            preloadChunks?: number;
            gutterTiles?: number;
        },
    ): BoardChunk[] {
        const rebuildStart = performance.now();
        const preloadChunks = options?.preloadChunks ?? TERRAIN_CHUNK_PRELOAD;
        const gutterTiles = options?.gutterTiles ?? TERRAIN_CHUNK_GUTTER_TILES;
        const visibleChunks = this.board.getVisibleChunks(viewport, preloadChunks, gutterTiles);
        const rebuiltChunkKeys = this.rebuildVisibleDirtyChunks(visibleChunks);
        const rebuildMs = performance.now() - rebuildStart;
        this.lastDebugStats = {
            chunkTileSize: this.board.getChunkTileSize(),
            visibleChunks: visibleChunks.length,
            rebuiltChunks: rebuiltChunkKeys.length,
            rebuiltChunkKeys,
            pendingTextureTiles: this.lastDebugStats.pendingTextureTiles,
            rebuildMs,
            drawMs: 0,
            drawBlitCount: 0,
            drawBlitArea: 0,
            maxBlitArea: 0,
            authoredMs: this.lastDebugStats.authoredMs,
            terrainBaseMs: this.lastDebugStats.terrainBaseMs,
            squarePathMs: this.lastDebugStats.squarePathMs,
            gridStrokeMs: this.lastDebugStats.gridStrokeMs,
            pathTileLayerMs: this.lastDebugStats.pathTileLayerMs,
        };
        return visibleChunks;
    }

    public destroy(): void {
        this.unsubscribeTerrainTextureUpdates();
    }

    public syncCanvasDimensions(viewportWidth: number, viewportHeight: number): void {
        this.viewportWidth = Math.max(1, Math.round(viewportWidth));
        this.viewportHeight = Math.max(1, Math.round(viewportHeight));
        this.devicePixelRatio =
            typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
                ? Math.max(1, window.devicePixelRatio)
                : 1;

        this.backgroundCanvas.style.width = `${this.viewportWidth}px`;
        this.backgroundCanvas.style.height = `${this.viewportHeight}px`;
        this.backgroundCanvas.width = Math.max(1, Math.round(this.viewportWidth * this.devicePixelRatio));
        this.backgroundCanvas.height = Math.max(1, Math.round(this.viewportHeight * this.devicePixelRatio));
        this.backgroundCanvas.style.transform = 'translate3d(0, 0, 0)';
        if (this.backgroundCtx) {
            this.backgroundCtx.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
            this.backgroundCtx.imageSmoothingEnabled = false;
        }

        this.requestRender();
    }

    public drawBackground(viewport: Viewport, options?: { preloadChunks?: number }): void {
        if (!this.backgroundCtx) {
            return;
        }

        const visibleChunks = this.prepareVisibleChunks(viewport, options);
        const rebuildMs = this.lastDebugStats.rebuildMs;
        const rebuiltChunks = this.lastDebugStats.rebuiltChunks;
        const rebuiltChunkKeys = this.lastDebugStats.rebuiltChunkKeys;

        const drawStart = performance.now();
        this.backgroundCtx.clearRect(0, 0, this.viewportWidth, this.viewportHeight);
        const offset = viewport.getCameraOffset();
        const visibleWorldRect = viewport.getVisibleWorldRect();
        let drawBlitCount = 0;
        let drawBlitArea = 0;
        let maxBlitArea = 0;
        for (const chunk of visibleChunks) {
            const texture = this.chunkTextures.get(chunk.key);
            if (!texture) {
                continue;
            }
            const intersection = intersectWorldRects(chunk.contentWorldBounds, visibleWorldRect);
            if (!intersection) {
                continue;
            }
            const intersectionArea = intersection.width * intersection.height;
            drawBlitCount += 1;
            drawBlitArea += intersectionArea;
            if (intersectionArea > maxBlitArea) {
                maxBlitArea = intersectionArea;
            }
            const sourceX = (intersection.left - chunk.worldBounds.left) * this.devicePixelRatio;
            const sourceY = (intersection.top - chunk.worldBounds.top) * this.devicePixelRatio;
            const sourceWidth = intersection.width * this.devicePixelRatio;
            const sourceHeight = intersection.height * this.devicePixelRatio;
            this.backgroundCtx.drawImage(
                texture,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                intersection.left + offset.x,
                intersection.top + offset.y,
                intersection.width,
                intersection.height,
            );
        }
        const drawMs = performance.now() - drawStart;
        this.lastDebugStats = {
            chunkTileSize: this.board.getChunkTileSize(),
            visibleChunks: visibleChunks.length,
            rebuiltChunks,
            rebuiltChunkKeys,
            pendingTextureTiles: this.lastDebugStats.pendingTextureTiles,
            rebuildMs,
            drawMs,
            drawBlitCount,
            drawBlitArea,
            maxBlitArea,
            authoredMs: this.lastDebugStats.authoredMs,
            terrainBaseMs: this.lastDebugStats.terrainBaseMs,
            squarePathMs: this.lastDebugStats.squarePathMs,
            gridStrokeMs: this.lastDebugStats.gridStrokeMs,
            pathTileLayerMs: this.lastDebugStats.pathTileLayerMs,
        };
    }

    public getDebugStats(): TerrainRenderDebugStats {
        return this.lastDebugStats;
    }

    private createBoard(map: MapDefinition, grid: GridGeometry, tileSize: number): RenderBoard {
        return new RenderBoard(
            map,
            grid,
            tileSize,
            resolveChunkTileSize(tileSize, {
                targetChunkPixelSize: TERRAIN_TARGET_CHUNK_PIXEL_SIZE,
                preferredChunkSizes: TERRAIN_PREFERRED_CHUNK_TILE_SIZES,
                ...this.chunkTileSizeOptions,
            }),
        );
    }

    private rebuildVisibleDirtyChunks(visibleChunks: readonly BoardChunk[]): string[] {
        const phaseTotals: TerrainChunkPhaseStats = {
            authoredMs: 0,
            terrainBaseMs: 0,
            squarePathMs: 0,
            gridStrokeMs: 0,
            pathTileLayerMs: 0,
            pendingTextureTiles: 0,
        };
        const rebuiltChunkKeys: string[] = [];
        for (const chunk of visibleChunks) {
            if (!this.dirtyChunkKeys.has(chunk.key)) {
                continue;
            }
            const chunkStats = this.renderChunk(chunk);
            phaseTotals.authoredMs += chunkStats.authoredMs;
            phaseTotals.terrainBaseMs += chunkStats.terrainBaseMs;
            phaseTotals.squarePathMs += chunkStats.squarePathMs;
            phaseTotals.gridStrokeMs += chunkStats.gridStrokeMs;
            phaseTotals.pathTileLayerMs += chunkStats.pathTileLayerMs;
            phaseTotals.pendingTextureTiles += chunkStats.pendingTextureTiles;
            this.dirtyChunkKeys.delete(chunk.key);
            rebuiltChunkKeys.push(chunk.key);
        }
        this.lastDebugStats.authoredMs = phaseTotals.authoredMs;
        this.lastDebugStats.terrainBaseMs = phaseTotals.terrainBaseMs;
        this.lastDebugStats.squarePathMs = phaseTotals.squarePathMs;
        this.lastDebugStats.gridStrokeMs = phaseTotals.gridStrokeMs;
        this.lastDebugStats.pathTileLayerMs = phaseTotals.pathTileLayerMs;
        this.lastDebugStats.pendingTextureTiles = phaseTotals.pendingTextureTiles;
        return rebuiltChunkKeys;
    }

    private renderChunk(chunk: BoardChunk): TerrainChunkPhaseStats {
        const texture = this.getOrCreateChunkTexture(chunk);
        const context = texture.getContext('2d');
        if (!context) {
            console.error('[MapLayerRenderer] Failed to create chunk texture context');
            return {
                authoredMs: 0,
                terrainBaseMs: 0,
                squarePathMs: 0,
                gridStrokeMs: 0,
                pathTileLayerMs: 0,
                pendingTextureTiles: 0,
            };
        }

        context.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
        context.imageSmoothingEnabled = false;
        context.clearRect(0, 0, chunk.worldBounds.width, chunk.worldBounds.height);

        let authoredMs = 0;
        let terrainBaseMs = 0;
        let squarePathMs = 0;
        let gridStrokeMs = 0;
        let pathTileLayerMs = 0;
        let pendingTextureTiles = 0;

        const hasAuthoredTileMap = Boolean(this.map.authoredTileMap);
        if (hasAuthoredTileMap) {
            const authoredStart = performance.now();
            this.drawAuthoredTileMapChunk(context, chunk, this.map.authoredTileMap!);
            authoredMs += performance.now() - authoredStart;
        }

        const tilePhaseStart = performance.now();
        for (let y = chunk.tileBounds.startY; y < chunk.tileBounds.endY; y += 1) {
            for (let x = chunk.tileBounds.startX; x < chunk.tileBounds.endX; x += 1) {
                const tile = this.map.tiles[y]?.[x];
                if (!tile) {
                    continue;
                }
                const tileMetrics = this.board.getTileDrawMetrics(x, y);
                const localX = tileMetrics.originX - chunk.worldBounds.left;
                const localY = tileMetrics.originY - chunk.worldBounds.top;
                const cx = localX + this.tileSize / 2;
                const cy = localY + this.tileSize / 2;

                if (tile.terrain === 'portal') {
                    context.fillStyle = COLORS.tilePortal;
                    this.grid.fillTile(context, cx, cy, this.tileSize * 0.5, x, y);

                    context.fillStyle = COLORS.tilePortalGlow;
                    context.globalAlpha = 0.4;
                    this.grid.fillTile(context, cx, cy, this.tileSize * 0.35, x, y);
                    context.globalAlpha = 1.0;

                    const radius = this.tileSize * 0.25;
                    context.beginPath();
                    context.arc(localX + tileMetrics.centroidX, localY + tileMetrics.centroidY, radius, 0, Math.PI * 2);
                    context.fillStyle = COLORS.tilePortalGlow;
                    context.globalAlpha = 0.6;
                    context.fill();
                    context.globalAlpha = 1.0;
                } else if (!hasAuthoredTileMap) {
                    pendingTextureTiles += this.fillTerrainTile(context, tile, cx, cy, x, y);
                }

                if (this.map.gridType === 'square' && !hasAuthoredTileMap) {
                    drawSquarePathOverlay(context, {
                        pathLayer: this.map.pathLayer,
                        mapWidth: this.map.width,
                        mapHeight: this.map.height,
                        tileX: x,
                        tileY: y,
                        drawX: localX,
                        drawY: localY,
                        drawSize: this.tileSize,
                    });
                }

                context.strokeStyle = COLORS.gridLine;
                context.lineWidth = 1;
                this.grid.drawTilePath(context, cx, cy, this.tileSize * 0.5, x, y);
                context.stroke();
            }
        }
        terrainBaseMs += performance.now() - tilePhaseStart;

        const pathTileLayerStart = performance.now();
        this.drawPathTileLayersChunk(context, chunk);
        pathTileLayerMs += performance.now() - pathTileLayerStart;
        return {
            authoredMs,
            terrainBaseMs,
            squarePathMs,
            gridStrokeMs,
            pathTileLayerMs,
            pendingTextureTiles,
        };
    }

    private drawPathTileLayersChunk(context: CanvasRenderingContext2D, chunk: BoardChunk): void {
        for (const layer of this.map.pathLayers ?? []) {
            const atlas = getPathTileQuarterAtlasDefinition(layer.atlasId as never, this.map.gridType);
            const image = this.pathTileAtlasImages.get(this.getPathAtlasCacheKey(layer.atlasId));
            if (!atlas) {
                continue;
            }
            for (let y = chunk.tileBounds.startY; y < Math.min(chunk.tileBounds.endY, layer.height); y += 1) {
                for (let x = chunk.tileBounds.startX; x < Math.min(chunk.tileBounds.endX, layer.width); x += 1) {
                    const cell = layer.data[(y * layer.width) + x];
                    if (!cell) {
                        continue;
                    }
                    const tileMetrics = this.board.getTileDrawMetrics(x, y);
                    const { drawWidth, drawHeight, drawX, drawY } = this.getPathTileDrawBox(
                        tileMetrics,
                        chunk,
                        atlas.drawScale ?? 1,
                    );
                    const renderTile = pathAutotiler.resolveRenderTile(
                        this.map,
                        layer.atlasId as never,
                        x,
                        y,
                        cell,
                        atlas,
                        image ?? null,
                    );
                    if (!renderTile) {
                        continue;
                    }
                    if (renderTile.kind === 'image') {
                        const cellRotationSteps = cell.mode === 'fixed' ? (cell.rotationSteps ?? 0) : 0;
                        const cellFlipHorizontal = cell.mode === 'fixed' && cell.flipHorizontal === true;
                        const cellFlipVertical = cell.mode === 'fixed' && cell.flipVertical === true;
                        const transformCenter = resolvePathTileTransformCenter(this.map.gridType, tileMetrics);
                        if (this.map.gridType === 'hex' || this.map.gridType === 'triangle') {
                            drawGridClippedPathTileImage({
                                context,
                                grid: this.grid,
                                gridType: this.map.gridType,
                                tileSize: this.tileSize,
                                tileX: x,
                                tileY: y,
                                boxCenterX: drawX + drawWidth / 2,
                                boxCenterY: drawY + drawHeight / 2,
                                transformCenterX: renderTile.applyCellTransform && transformCenter
                                    ? (transformCenter.x - chunk.worldBounds.left)
                                    : undefined,
                                transformCenterY: renderTile.applyCellTransform && transformCenter
                                    ? (transformCenter.y - chunk.worldBounds.top)
                                    : undefined,
                                drawWidth,
                                drawHeight,
                                image: renderTile.image,
                                rotationSteps: this.map.gridType === 'triangle' && renderTile.applyCellTransform
                                    ? cellRotationSteps
                                    : 0,
                                flipHorizontal: renderTile.applyCellTransform ? cellFlipHorizontal : false,
                                flipVertical: renderTile.applyCellTransform ? cellFlipVertical : false,
                            });
                        } else if (renderTile.applyCellTransform) {
                            context.save();
                            context.translate(drawX + drawWidth / 2, drawY + drawHeight / 2);
                            if (cellRotationSteps !== 0) {
                                context.rotate((((cellRotationSteps % 12) + 12) % 12) * (Math.PI / 6));
                            }
                            context.scale(cellFlipHorizontal ? -1 : 1, cellFlipVertical ? -1 : 1);
                            context.drawImage(renderTile.image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
                            context.restore();
                        } else {
                            context.drawImage(renderTile.image, drawX, drawY, drawWidth, drawHeight);
                        }
                        continue;
                    }
                    if (!image) {
                        void this.ensurePathTileAtlasLoaded(layer.atlasId as never);
                        continue;
                    }
                    const rotationSteps = renderTile.rotationSteps
                        + (renderTile.flipHorizontal || renderTile.flipVertical
                            ? (cell.mode === 'fixed' ? (cell.rotationSteps ?? 0) : 0)
                            : 0);
                    const sourceX = (renderTile.tileIndex % atlas.columns) * atlas.tileSize;
                    const sourceY = Math.floor(renderTile.tileIndex / atlas.columns) * atlas.tileSize;
                    if (this.map.gridType === 'hex' || this.map.gridType === 'triangle') {
                        drawGridClippedPathTileImage({
                            context,
                            grid: this.grid,
                            gridType: this.map.gridType,
                            tileSize: this.tileSize,
                            tileX: x,
                            tileY: y,
                            boxCenterX: drawX + drawWidth / 2,
                            boxCenterY: drawY + drawHeight / 2,
                            drawWidth,
                            drawHeight,
                            image,
                            sourceX,
                            sourceY,
                            sourceWidth: atlas.tileSize,
                            sourceHeight: atlas.tileSize,
                            rotationSteps,
                            flipHorizontal: renderTile.flipHorizontal,
                            flipVertical: renderTile.flipVertical,
                        });
                        continue;
                    }
                    context.save();
                    context.translate(drawX + drawWidth / 2, drawY + drawHeight / 2);
                    if (rotationSteps !== 0) {
                        context.rotate(((((rotationSteps % 12) + 12) % 12) * Math.PI) / 6);
                    }
                    context.scale(renderTile.flipHorizontal ? -1 : 1, renderTile.flipVertical ? -1 : 1);
                    context.drawImage(
                        image,
                        sourceX,
                        sourceY,
                        atlas.tileSize,
                        atlas.tileSize,
                        -drawWidth / 2,
                        -drawHeight / 2,
                        drawWidth,
                        drawHeight,
                    );
                    context.restore();
                }
            }
        }
    }

    private getPathAtlasCacheKey(atlasId: string): string {
        return `${this.map.gridType}:${atlasId}`;
    }

    private async ensurePathTileAtlasLoaded(atlasId: import('../../terrain/pathArtStyles').PathTileAtlasId): Promise<void> {
        const cacheKey = this.getPathAtlasCacheKey(atlasId);
        if (this.pathTileAtlasImages.has(cacheKey)) {
            return;
        }
        const image = await loadPathTileAtlasImage(atlasId, this.map.gridType);
        this.pathTileAtlasImages.set(cacheKey, image);
        this.invalidateMapTexture();
    }

    private async preloadPathTileAtlases(): Promise<void> {
        const atlasIds = new Set((this.map.pathLayers ?? []).map((layer) => layer.atlasId as import('../../terrain/pathArtStyles').PathTileAtlasId));
        await Promise.all(Array.from(atlasIds, (atlasId) => this.ensurePathTileAtlasLoaded(atlasId)));
    }

    private getPathTileDrawBox(
        tileMetrics: { centerX: number; centerY: number; spanWidth: number; spanHeight: number; originX: number; originY: number },
        chunk: BoardChunk,
        drawScale = 1,
    ): { drawWidth: number; drawHeight: number; drawX: number; drawY: number } {
        const { drawWidth, drawHeight, boxCenterX, boxCenterY } = resolvePathTileDrawGeometry(
            this.map.gridType,
            this.tileSize,
            tileMetrics,
            drawScale,
        );

        return {
            drawWidth,
            drawHeight,
            drawX: boxCenterX - drawWidth / 2 - chunk.worldBounds.left,
            drawY: boxCenterY - drawHeight / 2 - chunk.worldBounds.top,
        };
    }

    private drawAuthoredTileMapChunk(
        context: CanvasRenderingContext2D,
        chunk: BoardChunk,
        authoredTileMap: NonNullable<MapDefinition['authoredTileMap']>
    ): void {
        for (const layer of authoredTileMap.layers) {
            for (let y = chunk.tileBounds.startY; y < Math.min(chunk.tileBounds.endY, layer.height); y += 1) {
                for (let x = chunk.tileBounds.startX; x < Math.min(chunk.tileBounds.endX, layer.width); x += 1) {
                    const tile = layer.tiles[y * layer.width + x];
                    if (!tile || tile.gid <= 0) {
                        continue;
                    }
                    this.drawAuthoredTileMapTile(context, chunk, authoredTileMap, layer.name, tile, x, y);
                }
            }
        }
    }

    private drawAuthoredTileMapTile(
        context: CanvasRenderingContext2D,
        chunk: BoardChunk,
        authoredTileMap: NonNullable<MapDefinition['authoredTileMap']>,
        layerName: string,
        tile: NonNullable<MapDefinition['authoredTileMap']>['layers'][number]['tiles'][number],
        tileX: number,
        tileY: number
    ): void {
        const tileMetrics = this.board.getTileDrawMetrics(tileX, tileY);
        const drawWidth =
            typeof authoredTileMap.displayTileWidth === 'number'
                ? tileMetrics.spanWidth * (authoredTileMap.displayTileWidth / 32)
                : tileMetrics.spanWidth;
        const drawHeight =
            typeof authoredTileMap.displayTileHeight === 'number'
                ? tileMetrics.spanHeight * (authoredTileMap.displayTileHeight / 32)
                : tileMetrics.spanHeight;
        const drawX = tileMetrics.centerX - drawWidth / 2 - chunk.worldBounds.left;
        const drawY = tileMetrics.centerY - drawHeight / 2 - chunk.worldBounds.top;
        const directImage = this.resolveDirectAuthoredTileImage(authoredTileMap, layerName, tile.gid, tileX, tileY);

        context.save();
        context.imageSmoothingEnabled = false;
        context.translate(drawX + drawWidth / 2, drawY + drawHeight / 2);
        const matrix = this.buildTiledTransformMatrix(tile.flipHorizontal, tile.flipVertical, tile.flipDiagonal);
        context.transform(matrix.a, matrix.b, matrix.c, matrix.d, 0, 0);
        if (directImage) {
            context.drawImage(
                directImage,
                -drawWidth / 2,
                -drawHeight / 2,
                drawWidth,
                drawHeight
            );
        } else if (authoredTileMap.image && typeof authoredTileMap.columns === 'number') {
            const sourceIndex = tile.gid - 1;
            if (sourceIndex >= 0) {
                const sourceX = (sourceIndex % authoredTileMap.columns) * authoredTileMap.tileWidth;
                const sourceY = Math.floor(sourceIndex / authoredTileMap.columns) * authoredTileMap.tileHeight;
                context.drawImage(
                    authoredTileMap.image,
                    sourceX,
                    sourceY,
                    authoredTileMap.tileWidth,
                    authoredTileMap.tileHeight,
                    -drawWidth / 2,
                    -drawHeight / 2,
                    drawWidth,
                    drawHeight
                );
            }
        }
        context.restore();
    }

    private resolveDirectAuthoredTileImage(
        authoredTileMap: NonNullable<MapDefinition['authoredTileMap']>,
        layerName: string,
        gid: number,
        tileX: number,
        tileY: number,
    ): HTMLImageElement | null {
        const layerMappings = authoredTileMap.directTileImages?.[layerName];
        if (!layerMappings) {
            return null;
        }
        const gidKey = String(gid);
        if (layerMappings[gidKey]) {
            return layerMappings[gidKey];
        }
        const mapTile = this.map.tiles[tileY]?.[tileX];
        if (layerMappings.walkable && mapTile && mapTile.terrain !== 'wall') {
            return layerMappings.walkable;
        }
        if (layerMappings.blocked && mapTile?.terrain === 'wall') {
            return layerMappings.blocked;
        }
        return layerMappings.default ?? null;
    }

    private buildTiledTransformMatrix(
        flipHorizontal: boolean,
        flipVertical: boolean,
        flipDiagonal: boolean
    ): { a: number; b: number; c: number; d: number } {
        let matrix = { a: 1, b: 0, c: 0, d: 1 };
        if (flipDiagonal) {
            matrix = multiplyTransformMatrices({ a: 0, b: 1, c: 1, d: 0 }, matrix);
        }
        if (flipHorizontal) {
            matrix = multiplyTransformMatrices({ a: -1, b: 0, c: 0, d: 1 }, matrix);
        }
        if (flipVertical) {
            matrix = multiplyTransformMatrices({ a: 1, b: 0, c: 0, d: -1 }, matrix);
        }
        return matrix;
    }

    private fillTerrainTile(
        context: CanvasRenderingContext2D,
        tile: MapDefinition['tiles'][number][number],
        cx: number,
        cy: number,
        gridX: number,
        gridY: number
    ): number {
        context.fillStyle = this.getFallbackTileColor(tile);
        this.grid.fillTile(context, cx, cy, this.tileSize * 0.5, gridX, gridY);

        const textureKey = getTileTextureKey(tile);
        const texturePath = textureKey ? getTerrainTexturePath(textureKey) : null;
        const texture = textureKey ? getTerrainTexture(textureKey) : null;
        if (!texture) {
            if (texturePath) {
                return 1;
            }
            return 0;
        }

        context.save();
        this.grid.drawTilePath(context, cx, cy, this.tileSize * 0.5, gridX, gridY);
        context.clip();
        const textureExtent = this.tileSize * 1.5;
        context.drawImage(
            texture,
            cx - textureExtent / 2,
            cy - textureExtent / 2,
            textureExtent,
            textureExtent
        );
        context.restore();
        return 0;
    }

    private getFallbackTileColor(tile: MapDefinition['tiles'][number][number]): string {
        switch (tile.biome) {
            case 'grass':
            case 'soft_grass':
                return COLORS.tileGrass;
            case 'sand':
                return COLORS.tileSand;
            case 'water':
                return COLORS.tileWater;
            case 'dirt':
                return COLORS.tileDirt;
            case 'grassy_dirt':
                return '#c7a56a';
            case 'brick':
                return '#b45309';
            case 'brick2':
                return '#92400e';
            case 'cobblestone_path':
                return '#94a3b8';
            case 'realistic_grass':
            case 'realistic_grass2':
                return COLORS.tileGrass;
            case 'realistic_floor':
                return COLORS.tileFloor;
            case 'realistic_water':
                return COLORS.tileWater;
            case 'realistic_dirt':
                return COLORS.tileDirt;
            case 'default':
            default:
                return tile.walkable ? COLORS.tileFloor : COLORS.tileWall;
        }
    }

    private getOrCreateChunkTexture(chunk: BoardChunk): HTMLCanvasElement {
        const existing = this.chunkTextures.get(chunk.key);
        const backingWidth = Math.max(1, Math.round(chunk.worldBounds.width * this.devicePixelRatio));
        const backingHeight = Math.max(1, Math.round(chunk.worldBounds.height * this.devicePixelRatio));
        if (existing && existing.width === backingWidth && existing.height === backingHeight) {
            return existing;
        }

        const canvas = document.createElement('canvas');
        canvas.width = backingWidth;
        canvas.height = backingHeight;
        this.chunkTextures.set(chunk.key, canvas);
        return canvas;
    }

    private invalidateAllChunks(): void {
        const { cols, rows } = this.board.getChunkCount();
        for (let chunkY = 0; chunkY < rows; chunkY += 1) {
            for (let chunkX = 0; chunkX < cols; chunkX += 1) {
                this.dirtyChunkKeys.add(this.board.getChunkKey(chunkX, chunkY));
            }
        }
        this.requestRender();
    }

    private rebuildTextureChunkIndex(): void {
        this.textureChunkKeys.clear();
        const chunkTileSize = this.board.getChunkTileSize();
        for (let y = 0; y < this.map.height; y += 1) {
            for (let x = 0; x < this.map.width; x += 1) {
                const tile = this.map.tiles[y]?.[x];
                const textureKey = tile ? getTileTextureKey(tile) : null;
                if (!textureKey) {
                    continue;
                }
                const chunkKey = this.board.getChunkKey(
                    Math.floor(x / chunkTileSize),
                    Math.floor(y / chunkTileSize),
                );
                const chunkKeys = this.textureChunkKeys.get(textureKey);
                if (chunkKeys) {
                    chunkKeys.add(chunkKey);
                } else {
                    this.textureChunkKeys.set(textureKey, new Set([chunkKey]));
                }
            }
        }
    }

    private invalidateTextureChunks(textureKey: TerrainTextureKey): void {
        const chunkKeys = this.textureChunkKeys.get(textureKey);
        if (!chunkKeys || chunkKeys.size === 0) {
            return;
        }

        let changed = false;
        for (const chunkKey of chunkKeys) {
            if (!this.dirtyChunkKeys.has(chunkKey)) {
                this.dirtyChunkKeys.add(chunkKey);
                changed = true;
            }
        }

        if (changed) {
            this.requestRender();
        }
    }
}

function multiplyTransformMatrices(
    left: { a: number; b: number; c: number; d: number },
    right: { a: number; b: number; c: number; d: number }
): { a: number; b: number; c: number; d: number } {
    return {
        a: left.a * right.a + left.c * right.b,
        b: left.b * right.a + left.d * right.b,
        c: left.a * right.c + left.c * right.d,
        d: left.b * right.c + left.d * right.d,
    };
}
